import capabilities, {
	CYCLIC_BUCKETS,
	COMPARE_FORMS,
	DEFAULT_BINS,
	DOOR_REASONS,
	FORM_OPTIONS,
	KQL_SYSTEM_NAMES,
	LIMITS,
	OP_VERBS,
	PART_OF_WHOLE_OPS,
	describeColumnForCharts,
	describeElementForCharts,
	describeSystemField,
	doorFor,
	drawnCount,
	estimateBuckets,
	formsForShape,
	measureAnswer,
	refusalFor,
} from '../../utils/analytics';
import { RANGE_PRESET_LABELS, normalizeRange, rangeLabel } from '../../utils/chartRanges';
import { chartFormWord } from '../../utils/chartForms';
import { allowedCodes, defaultCodeOf, exponentOf, getCurrency } from '../../utils/currency';
import { getUnit, isUnitOf } from '../../utils/units';
import { normalizeId } from '../../utils/ids';
import { buildFormulaTokens } from '../../utils/formulaTokens';
import { choiceListOf } from '../../utils/choiceValues';
import {
	GONE_FIELD_WORDS, columnForField, columnRefFor, columnRefOf, columnsOfGroup, elementForField, fieldIdOf, fieldRefFor,
} from '../../utils/chartSpecFields';
import {
	RESULT_WORDS, checkAcross, checkPerItem, formulaElements, isFormulaMeasure, isPerItemMeasure, missingNames, namesIn,
	referable, takesFormulaSlot,
} from './formulaModel';

/**
 * THE CHART BUILDER'S MODEL (charts A5b, plan §6.2): the question a person
 * builds — the card, what to measure, how to group and split it, the filter,
 * the date and its range, the form and its options — held as a DRAFT, and
 * everything the screen needs to know about it worked out here, in pure
 * functions, so the builder offers exactly what the vendored capability table
 * allows (keepr-api `utils/analytics/capabilities.js`, the same table the
 * server's spec validator reads) and the tests read the rules without a
 * screen.
 *
 *   emptyDraft()                 a new chart: Count items, of every card, as a number
 *   draftFromSpec(spec)          an existing chart's spec, as a draft
 *   namedDraft(draft, ctx)       that draft's fields by name, once its card is known
 *   specFromDraft(draft, ctx)    the spec the preview, the save and the edit send
 *   reconcile(draft, ctx)        clears what a later change made impossible, each with a one-line notice
 *   builderView(draft, ctx)      what each step offers, disables (and why), and shows
 *   refusalKey(path, view)       which control a server refusal's `path` belongs to
 *
 *   removeMeasure(draft, i, ctx)  drops a measure, clearing a formula that read it, with a notice
 *   clearFormulasForCard(draft, ctx)  a card switch: clears a calculation reading what the new card lacks
 *
 * `ctx` is the chart's world: `{ card, fields, cardName, elements, shadowed,
 * entries }` — chartFields() and the card's merged elements (what a per-item
 * formula reads), its ancestors' elements (what an id a restatement replaced
 * names), and the repeating group whose entries the chart counts, by name
 * (KPR-684; null counts items).
 *
 * A REPEATING GROUP'S ENTRIES (KPR-684). `draft.entries` names a group of the
 * card whose ENTRIES the chart counts (keepr-api spec.js `entries`); null
 * counts its items. A group's columns are fields named `<group>.<column>`
 * (an element name holds no dot), each described by the vendored table's
 * describeColumnForCharts for what the chart counts: counting entries, the
 * counted group's columns measure and group, and the item's own elements only
 * group (each entry placed by its item's value — measured, one would count
 * once per entry); counting items, every group's columns only group.
 *
 * THE SPEC FIELDS BY ID (KPR-238 P6, utils/chartSpecFields.js). A stored spec
 * may name an element by id, `#k7f3q2xa`, in a measure's element and the `on`
 * of Group by, Split by and the date. The draft always holds NAMES — the card's
 * element names, unique on it — so every rule below compares fields as text:
 * namedDraft turns a stored id into its element's name when the chart opens
 * (one the card no longer holds stays as it was, and is cleared with a line
 * that says "an element that is no longer here", never the id), and
 * specFromDraft writes each element back as its id while the builder writes
 * ids (SpecIdWrites.ON, deploy 2), else by name.
 *
 * A measure is one of four kinds:
 *   - an element measure ("Total of Amount") or Count items;
 *   - a per-item calculation (`perItem`, "Calculate per item…", A8): a
 *     formula over each item's elements, measured like an element of its
 *     result type — the vendored capability table offers its verbs;
 *   - a formula across measures (`formula`, "Add formula measure", A8):
 *     worked out per bucket from the measures above it, which it names by key
 *     and the editor shows as pills by their labels;
 *   - anything else a spec may hold that the builder cannot edit, carried
 *     through untouched (`keep`).
 * Formulas are checked by formulaModel.js, in the server's own words.
 *
 * Things this model never decides on its own: whether an element collides
 * with another card's in the collection's filter vocabulary (the server reads
 * a vocabulary the client cannot see whole, and refuses with
 * `element_type_collision` on the field — the builder then marks that element,
 * `refusedElements`).
 */

const has = (list, value) => Array.isArray(list) && list.indexOf(value) !== -1;
const own = (obj, key) => Boolean(obj) && Object.prototype.hasOwnProperty.call(obj, key);
const GROUP_TYPE = 'repeating-group';

// ------------------------------------------------------------- the words

/** The system fields a chart may use, in the order the builder lists them. "Created by" exists once KQL has its creator term (PR AK). */
export const CHART_SYSTEM_NAMES = Object.freeze(['created', 'updated', 'card', 'collection', 'created.by']);
const SYSTEM_LABELS = Object.freeze({ created: 'Created', updated: 'Updated', card: 'Card', collection: 'Collection', 'created.by': 'Created by' });
// Names KQL keeps for itself that are not a chart's system fields: an
// element called one may be measured, never grouped by or used as the date
// (the server's `field_reserved`), because its door would name the system
// field.
const RESERVED_NAMES = KQL_SYSTEM_NAMES.filter((name) => !has(CHART_SYSTEM_NAMES, name));

/** The order of the measure step's verbs (plan §6.2). */
export const MEASURE_OPS = Object.freeze(['count', 'sum', 'avg', 'median', 'min', 'max', 'latest', 'distinct', 'countNonBlank', 'countYes', 'shareYes']);
const VERBS = Object.freeze({
	count: 'Count items',
	sum: 'Total of',
	avg: 'Average of',
	median: 'Median of',
	min: 'Smallest',
	max: 'Largest',
	latest: 'Latest value of',
	distinct: 'Distinct',
	countNonBlank: 'Count of',
	countYes: 'Count of Yes in',
	shareYes: 'Share of Yes in',
});
const DATED_VERBS = Object.freeze({ min: 'Earliest', max: 'Latest' });
// What a measure does to its element, for "Choose the element to total."
const TO_VERBS = Object.freeze({
	sum: 'total',
	avg: 'average',
	median: 'take the median of',
	min: 'find the smallest or earliest of',
	max: 'find the largest or latest of',
	latest: 'take the latest value of',
	distinct: 'count the distinct values of',
	countNonBlank: 'count',
	countYes: 'count the Yes answers of',
	shareYes: 'take the share of Yes of',
});
const DATED_KINDS = ['date', 'datetime', 'time'];

export const BUCKET_WORDS = Object.freeze({
	hour: 'Hour',
	day: 'Day',
	week: 'Week',
	month: 'Month',
	quarter: 'Quarter',
	year: 'Year',
	weekday: 'Day of the week',
	hourOfDay: 'Hour of the day',
	monthOfYear: 'Month of the year',
	halfHour: '30-minute slot',
});
const bucketPhrase = (bucket) => (own(BUCKET_WORDS, bucket) ? BUCKET_WORDS[bucket].toLowerCase() : bucket);

export const MULTI_WORDS = Object.freeze({
	earliest: 'Use the earliest',
	latest: 'Use the latest',
	each: 'Each date in its own bar',
});

/** The goal's own words per form option (§7). */
export const ORIENTATION_WORDS = Object.freeze({ auto: 'Automatic', vertical: 'Vertical', horizontal: 'Horizontal' });
export const STACK_WORDS = Object.freeze({ none: 'Side by side', stacked: 'Stacked', percent: '100%' });
export const SORT_WORDS = Object.freeze({ group: 'In their order', value: 'By value' });

/** What a person calls an element: its singular label, else its name. */
export function elementLabel(element) {
	const singular = element && element.label && typeof element.label.singular === 'string' ? element.label.singular.trim() : '';
	if (singular) return singular;
	return element && typeof element.name === 'string' ? element.name : '';
}

/** What a person calls a card. */
export function cardLabel(card) {
	if (!card) return '';
	const singular = card.label && typeof card.label.singular === 'string' ? card.label.singular.trim() : '';
	return singular || (typeof card.name === 'string' ? card.name : '') || 'card';
}

// --------------------------------------------------------------- fields

/**
 * Every field a chart of this card may name: the system fields the vendored
 * table describes (created, updated, card, collection — and "Created by" once
 * it does), then the card's own elements (its own, its parents', its element
 * sets', as the card cache merges them), each with its capability
 * description. `card` null is a chart of every card: system fields only
 * (ruled d20).
 *
 * An element named like a chart's system field is never listed: the chart
 * reads the system field first, as unquoted KQL does. An element named like
 * one of KQL's other reserved names (`set`, `tags`, `notes`, `id`) is
 * listed, measurable only.
 */
export function chartFields({ card = null, elements = [], cardHasDescendants = false, hasSubCollections = false, entries = null, time = null } = {}) {
	const cardId = card ? normalizeId(card._id) || null : null;
	const ctx = { cardId, cardHasDescendants, hasSubCollections };
	const list = Array.isArray(elements) ? elements : [];
	// Counting a group's entries (KPR-684): only a group this card holds.
	const counted = cardId && entries ? list.find((el) => el && el.name === entries && el.dataType === GROUP_TYPE) || null : null;
	// The chart's date a date column of the counted group (keepr-api KPR-806,
	// spec.js `entryDate`): each entry has a date of its own, so a column's
	// latest value is the newest entry's.
	const timeColumn = counted && typeof time === 'string' && columnRefOf(time) ? columnForField(list, time) : null;
	const entryDate = Boolean(timeColumn && timeColumn.group.name === counted.name
		&& describeColumnForCharts(timeColumn.column, counted, { counting: 'entries' }).time === true);
	const countedLabel = counted ? elementLabel(counted) || counted.name : '';
	// Counting entries, the item's own value is grouped by, never measured:
	// it would count once for each entry (keepr-api's measure_not_on_entries).
	const itemOwn = (field) => (counted ? {
		...field,
		description: { ...field.description, measures: [], defaultMeasure: null },
		entriesReason: `${field.label} belongs to the item, so a chart counting ${countedLabel} entries would count it once for each entry. Measure a column of ${countedLabel}, or count items.`,
	} : field);
	const out = [];
	CHART_SYSTEM_NAMES.forEach((name) => {
		const description = describeSystemField(name, ctx);
		if (!description) return;
		out.push(itemOwn({ name, label: SYSTEM_LABELS[name], system: true, reserved: false, element: null, description }));
	});
	if (!cardId) return out;
	const seen = new Set();
	const columns = [];
	list.forEach((element) => {
		const name = element && typeof element.name === 'string' ? element.name : '';
		if (!name || seen.has(name) || has(CHART_SYSTEM_NAMES, name)) return;
		seen.add(name);
		out.push(itemOwn({
			name,
			label: elementLabel(element) || name,
			system: false,
			reserved: has(RESERVED_NAMES, name),
			element,
			description: describeElementForCharts(element),
		}));
		// A repeating group's columns, after the card's own elements: every
		// group's while counting items, the counted group's alone while
		// counting entries.
		if (element.dataType !== GROUP_TYPE || (counted && counted.name !== name)) return;
		const counting = counted ? 'entries' : 'items';
		const groupLabel = elementLabel(element) || name;
		columnsOfGroup(element).forEach((column) => {
			columns.push({
				name: `${name}.${column.name}`,
				label: `${groupLabel} › ${elementLabel(column) || column.name}`,
				system: false,
				reserved: false,
				element: column,
				group: element,
				column: true,
				description: describeColumnForCharts(column, element, { counting, entryDate }),
			});
		});
	});
	return [...out, ...columns];
}

/** The repeating groups of a card a chart can count the entries of (KPR-684): `[{ name, label }]`. */
export function countableGroups(elements = []) {
	return (Array.isArray(elements) ? elements : [])
		.filter((el) => el && el.dataType === GROUP_TYPE && typeof el.name === 'string' && el.name && !has(CHART_SYSTEM_NAMES, el.name))
		.map((el) => ({ name: el.name, label: elementLabel(el) || el.name }));
}

/** The field a spec field names: by name, or — `#k7f3q2xa` — the card's element of that id (elementForField). */
function fieldNamed(ctx, name) {
	if (!ctx || !Array.isArray(ctx.fields) || typeof name !== 'string') return null;
	// A repeating group's column (KPR-684), its parts by name or by id.
	if (columnRefOf(name)) {
		const col = columnForField(ctx.elements, name, { shadowed: ctx.shadowed });
		const key = col ? `${col.group.name}.${col.column.name}` : name;
		return ctx.fields.find((f) => f.column && f.name === key) || null;
	}
	if (fieldIdOf(name) !== null) {
		const el = elementForField(ctx.elements, name, { shadowed: ctx.shadowed });
		return el ? ctx.fields.find((f) => !f.system && f.name === el.name) || null : null;
	}
	return ctx.fields.find((f) => f.name === name) || null;
}

/** The words for a spec field the card does not have: its name — never an id. */
const fieldWords = (ref) => (fieldIdOf(ref) !== null ? GONE_FIELD_WORDS : ref);

/** How a spec field is written: an element by fieldRefFor (its id while the builder writes ids), a system field as it is. */
function specRef(ctx, name) {
	const field = fieldNamed(ctx, name);
	if (field && field.column) return columnRefFor(field.group, field.element) || name;
	return field && !field.system && field.element ? fieldRefFor(field.element) || name : name;
}

/** The group a chart counting entries counts (KPR-684), on this card, or null. */
function countedGroup(ctx) {
	if (!ctx || !ctx.entries || !Array.isArray(ctx.elements)) return null;
	return ctx.elements.find((el) => el && el.name === ctx.entries && el.dataType === GROUP_TYPE) || null;
}

/** keepr-api's per_item_on_entries, in its words. */
function perItemOnEntries(ctx) {
	const group = countedGroup(ctx);
	const label = group ? elementLabel(group) || group.name : 'its';
	return `A calculation per item can't be measured in a chart that counts ${label} entries yet. Measure a column of ${label}, or count items.`;
}

/**
 * Why a field the draft names is not on this chart's list: a column of a
 * group other than the one counted gets keepr-api's column_of_another_group
 * words (the review's finding 7); anything else, that the card has no such
 * field.
 */
function missingFieldWords(ctx, name) {
	const col = columnRefOf(name) && ctx ? columnForField(ctx.elements, name, { shadowed: ctx.shadowed }) : null;
	const counted = countedGroup(ctx);
	if (col && counted && counted.name !== col.group.name) {
		const label = elementLabel(col.column) || col.column.name;
		const groupLabel = elementLabel(col.group) || col.group.name;
		return `${label} is a column of ${groupLabel}, and this chart counts ${elementLabel(counted) || counted.name} entries.`;
	}
	return null;
}

/** "Count items", or — counting a group's entries — "Count entries" (keepr-api plan.js). */
const countWords = (ctx) => (countedGroup(ctx) ? 'Count entries' : VERBS.count);
const groupOfField = (field) => (field && field.description && field.description.groups && field.description.groups[0]) || null;
const isGroupable = (field) => Boolean(field) && !field.reserved && Boolean(groupOfField(field));
const isTimeField = (field) => Boolean(field) && !field.reserved && field.description && field.description.time === true;
const isDated = (field) => Boolean(field) && has(DATED_KINDS, field.description && field.description.kind);

/** The axis a group lays out for a bucket: a cyclic part is an ordinal axis, whatever its group's own is (PR AK's bucketAxis). */
function axisOf(group, bucket) {
	if (typeof capabilities.bucketAxis === 'function') return capabilities.bucketAxis(group, bucket);
	if (has(CYCLIC_BUCKETS, bucket)) return 'ordinal';
	return group ? group.axis : null;
}

// ---------------------------------------------------------------- drafts

const firstFreeKey = (measures) => {
	const taken = new Set((measures || []).map((m) => m && m.key));
	for (let n = 1; n < 100; n += 1) {
		if (!taken.has(`m${n}`)) return `m${n}`;
	}
	return `m${Date.now()}`;
};

/** A plain measure as the builder holds it. */
export function newMeasure(measures = [], op = 'count') {
	return { key: firstFreeKey(measures), op, element: null, perItem: null, formula: null, blanks: 'omit', unit: null, currency: null, label: '', hidden: false, keep: null };
}

/**
 * A measure worked out per item ("Calculate per item…", A8): an empty
 * formula, Total of until the formula's result says otherwise — the verb
 * follows the result's own default until the person picks one (`opChosen`).
 */
export function newPerItemMeasure(measures = []) {
	return { ...newMeasure(measures, 'sum'), perItem: '', opChosen: false };
}

/**
 * A formula across measures ("Add formula measure", A8). Labelled, until the
 * person types a label, by its formula in words ("Total of Amount / Count
 * items") — the server would label it by its key. A stored one the author
 * never labelled keeps reading as its key (`keyLabel`), so it round-trips.
 */
export function newFormulaMeasure(measures = []) {
	return { ...newMeasure(measures, null), op: null, formula: '', keyLabel: false };
}

/** The measures with a plain measure added where a formula can read it: before the first formula across measures. */
export function withPlainMeasure(measures, measure) {
	const list = Array.isArray(measures) ? measures : [];
	const at = list.findIndex(takesFormulaSlot);
	return at === -1 ? [...list, measure] : [...list.slice(0, at), measure, ...list.slice(at)];
}

const EMPTY_SHOW = Object.freeze({
	type: 'number',
	chosen: false,
	goal: null,
	lowerIsBetter: false,
	sparkline: false,
	orientation: 'auto',
	stack: 'none',
	sort: 'group',
	cumulative: false,
	markers: false,
});

/** A group (or a split) as the builder holds it, with its field's defaults. */
export function newGroup(field) {
	const g = groupOfField(field);
	const out = {
		on: field ? field.name : null,
		bucket: g && g.defaultBucket ? g.defaultBucket : null,
		binsMode: 'count',
		binsCount: g && g.kind === 'bins' ? DEFAULT_BINS : null,
		binsSize: null,
		top: null,
		multi: field && field.description && Array.isArray(field.description.multi) ? 'earliest' : null,
		blanks: 'show',
	};
	return out;
}

/** A new chart: Count items of every card, as a number — a question that answers at once. */
export function emptyDraft() {
	return {
		cardId: null,
		subCollections: true,
		filter: '',
		// The repeating group whose entries the chart counts, by name (KPR-684); null counts items.
		entries: null,
		measures: [newMeasure([])],
		groupBy: null,
		splitBy: null,
		time: { on: null, multi: null, range: null, compare: false },
		show: { ...EMPTY_SHOW },
	};
}

const PLAIN_KEYS = ['key', 'op', 'element', 'unit', 'currency', 'blanks', 'label', 'hidden'];
const PER_ITEM_KEYS = ['key', 'op', 'perItem', 'blanks', 'label', 'hidden'];
const FORMULA_KEYS = ['key', 'formula', 'label'];

function groupFromSpec(raw) {
	if (!raw || typeof raw !== 'object' || typeof raw.on !== 'string') return null;
	const bins = raw.bins && typeof raw.bins === 'object' ? raw.bins : null;
	return {
		on: raw.on,
		bucket: typeof raw.bucket === 'string' ? raw.bucket : null,
		binsMode: bins && typeof bins.size === 'number' ? 'size' : 'count',
		binsCount: bins && typeof bins.count === 'number' ? bins.count : (bins ? null : DEFAULT_BINS),
		binsSize: bins && typeof bins.size === 'number' ? bins.size : null,
		top: typeof raw.top === 'number' ? raw.top : null,
		multi: typeof raw.multi === 'string' ? raw.multi : null,
		blanks: raw.blanks === 'hide' ? 'hide' : 'show',
	};
}

/**
 * What a measure works out, as text: its formula, or its verb and element.
 * A saved measure carries the one it was saved with (`origin`), so the
 * builder can tell what the person changed from what they opened (the A8
 * review's I-1: a saved calculation the run refuses is held, not blocked,
 * until the person changes it).
 */
function fingerprintOf(m) {
	if (isFormulaMeasure(m)) return JSON.stringify(['formula', m.formula]);
	if (isPerItemMeasure(m)) return JSON.stringify(['perItem', m.perItem]);
	return JSON.stringify(['element', m.op, m.element || null, m.unit || null, m.currency || null]);
}

/** Has the person changed what this measure works out since the chart was opened? A new measure has. */
const unchanged = (m) => Boolean(m) && !m.keep && typeof m.origin === 'string' && m.origin === fingerprintOf(m);

/** One stored measure as the builder holds it — or `keep`, the spec's own object, for a shape it cannot edit. */
function measureFromSpec(m) {
	const out = measureShapeFromSpec(m);
	if (out.keep) return out;
	return { ...out, origin: fingerprintOf(out) };
}

function measureShapeFromSpec(m) {
	const raw = m && typeof m === 'object' ? m : {};
	const keys = Object.keys(raw);
	const only = (list) => keys.every((k) => has(list, k));
	const key = typeof raw.key === 'string' ? raw.key : null;
	const label = typeof raw.label === 'string' ? raw.label : '';
	const labelOk = raw.label === undefined || typeof raw.label === 'string';
	const hiddenOk = raw.hidden === undefined || typeof raw.hidden === 'boolean';
	const base = { ...newMeasure([], null), key, label };
	const kept = { ...base, key: key || firstFreeKey([]), op: raw.op || null, label: '', keep: raw };
	if (!key || !labelOk) return kept;
	if (raw.formula !== undefined) {
		if (typeof raw.formula !== 'string' || !only(FORMULA_KEYS)) return kept;
		return { ...base, op: null, formula: raw.formula, keyLabel: !label };
	}
	if (raw.perItem !== undefined) {
		if (typeof raw.perItem !== 'string' || typeof raw.op !== 'string' || raw.op === 'count' || !only(PER_ITEM_KEYS) || !hiddenOk) return kept;
		return { ...base, op: raw.op, perItem: raw.perItem, opChosen: true, blanks: raw.blanks === 'zero' ? 'zero' : 'omit', hidden: raw.hidden === true, hideWish: raw.hidden === true };
	}
	if (typeof raw.op !== 'string' || !only(PLAIN_KEYS) || !hiddenOk) return kept;
	return {
		...base,
		op: raw.op,
		element: typeof raw.element === 'string' ? raw.element : null,
		blanks: raw.blanks === 'zero' ? 'zero' : 'omit',
		unit: typeof raw.unit === 'string' ? raw.unit : null,
		currency: typeof raw.currency === 'string' ? raw.currency : null,
		hidden: raw.hidden === true,
		hideWish: raw.hidden === true,
	};
}

/**
 * An existing chart's spec as a draft. Element measures, per-item
 * calculations, formulas across measures and hidden inputs (A8) are
 * editable; a measure in a shape the builder does not know — a key it does
 * not know — is carried as `keep`, the spec's own object, written back
 * exactly as it came. The goal comes back in the units a person types (a
 * share as a percent, money in its major units) once the measures are known:
 * goalFromSpec does that, given the fields.
 */
export function draftFromSpec(spec) {
	const s = spec && typeof spec === 'object' ? spec : {};
	const draft = emptyDraft();
	draft.cardId = typeof s.card_id === 'string' && s.card_id ? s.card_id : null;
	draft.subCollections = s.subCollections !== false;
	draft.filter = typeof s.filter === 'string' ? s.filter : '';
	draft.entries = typeof s.entries === 'string' && s.entries ? s.entries : null;
	const measures = Array.isArray(s.measures) ? s.measures : [];
	draft.measures = measures.map(measureFromSpec);
	if (!draft.measures.length) draft.measures = [newMeasure([])];
	draft.groupBy = groupFromSpec(s.groupBy);
	draft.splitBy = draft.groupBy ? groupFromSpec(s.splitBy) : null;
	const time = s.time && typeof s.time === 'object' ? s.time : null;
	draft.time = {
		on: time && typeof time.on === 'string' ? time.on : null,
		multi: time && typeof time.multi === 'string' ? time.multi : null,
		range: time ? (normalizeRange(time.range) ?? null) : null,
		compare: Boolean(time && time.compare === 'previous'),
	};
	if (draft.time.range && draft.time.range.preset === 'all') draft.time.range = null;
	const show = s.show && typeof s.show === 'object' ? s.show : {};
	draft.show = {
		...EMPTY_SHOW,
		type: typeof show.type === 'string' ? show.type : 'number',
		// A saved chart's form is its author's choice, never the recommendation's.
		chosen: true,
		goal: typeof show.goal === 'number' ? show.goal : null,
		lowerIsBetter: show.lowerIsBetter === true,
		sparkline: show.sparkline === true,
		orientation: typeof show.orientation === 'string' ? show.orientation : 'auto',
		stack: typeof show.stack === 'string' ? show.stack : 'none',
		sort: typeof show.sort === 'string' ? show.sort : 'group',
		cumulative: show.cumulative === true,
		markers: show.markers === true,
	};
	// The goal as stored is in the run's own units; goalFromSpec turns it into
	// the person's once the fields are known. Until then it is marked raw.
	draft.show.goalRaw = typeof show.goal === 'number';
	// The builder can't read what a measure it keeps answers, so such a chart
	// is offered the form it was saved with, and the table.
	if (draft.measures.some((m) => m.keep)) draft.show.savedType = draft.show.type;
	return draft;
}

/**
 * A draft from draftFromSpec with every field that names its element by id
 * (`#k7f3q2xa`, KPR-238 P6) written as that element's name on the chart's
 * card (`ctx`, its world) — so the builder works in names, and writes ids
 * back only at specFromDraft. A field naming an element the card no longer
 * holds is cleared, and `notices` (an array, when given) gets one line for it
 * — never the id, which a person cannot read, in a picker or in words. A kept
 * measure is the spec's own object, untouched. The same object when nothing
 * names an id.
 */
export function namedDraft(draft, ctx, notices = []) {
	if (!draft) return draft;
	const ids = [draft.entries, draft.time && draft.time.on, draft.groupBy && draft.groupBy.on, draft.splitBy && draft.splitBy.on, ...(draft.measures || []).map((m) => (m && !m.keep ? m.element : null))];
	// A column's parts may be ids too (KPR-684).
	const byId = (ref) => fieldIdOf(ref) !== null || (columnRefOf(ref) !== null && (fieldIdOf(columnRefOf(ref).group) !== null || fieldIdOf(columnRefOf(ref).column) !== null));
	if (!ids.some(byId)) return draft;
	const gone = (step, text) => notices.push({ step, text: `${text} named ${GONE_FIELD_WORDS}, so it was cleared. Choose another.` });
	const named = (ref) => {
		if (!byId(ref)) return ref;
		if (columnRefOf(ref)) {
			const col = columnForField(ctx.elements, ref, { shadowed: ctx.shadowed });
			return col ? `${col.group.name}.${col.column.name}` : null;
		}
		const field = fieldNamed(ctx, ref);
		return field ? field.name : null;
	};
	let entries = draft.entries;
	if (byId(entries)) {
		const el = elementForField(ctx.elements, entries, { shadowed: ctx.shadowed });
		entries = el && el.dataType === GROUP_TYPE ? el.name : null;
		if (entries === null) notices.push({ step: 'items', text: `The chart counted the entries of ${GONE_FIELD_WORDS}, so it counts items now.` });
	}
	const measures = (draft.measures || []).map((m, i) => {
		if (!m || m.keep || !byId(m.element)) return m;
		const next = { ...m, element: named(m.element) };
		if (next.element === null) gone('measures', `Measure ${i + 1}`);
		// The measure as opened is the one by name: a spelling is no change.
		if (typeof m.origin === 'string') next.origin = fingerprintOf(next);
		return next;
	});
	const group = (which, word) => {
		const g = draft[which];
		if (!g) return g;
		const on = named(g.on);
		if (on === null) { gone(which, word); return null; }
		return { ...g, on };
	};
	const groupBy = group('groupBy', 'Group by');
	const splitBy = groupBy ? group('splitBy', 'Split by') : null;
	if (!groupBy && draft.splitBy) notices.push({ step: 'splitBy', text: 'Split by was cleared: it needs a Group by.' });
	let time = draft.time;
	if (time && byId(time.on)) {
		const on = named(time.on);
		if (on === null) gone('time', 'The chart\'s date');
		// Cleared as reconcile clears a date the card lacks.
		time = on === null ? { ...time, on: null, multi: null, compare: false } : { ...time, on };
	}
	return { ...draft, entries, measures, time, groupBy, splitBy };
}

// --------------------------------------------------------------- answers

/**
 * What a measure answers (the capability table's measureAnswer, with A2b's
 * currency rule): an element measure from its element, a per-item
 * calculation from its formula's result (`check`, formulaModel), a formula
 * across measures a number. One whose formula is not finished answers
 * nothing yet (`result: null`).
 */
function answerOf(m, ctx, check = null) {
	if (!m || m.keep) {
		const keep = m && m.keep;
		return { op: keep && keep.op, result: 'number', axis: 'number', mixed: false, hidden: Boolean(keep && keep.hidden), keep: true };
	}
	const hidden = m.hidden === true;
	if (isFormulaMeasure(m)) {
		// The run computes only a formula that answers a number (an amount or a
		// measurement is `not_offered_yet`), on the number axis; a saved one it
		// refuses still answers what its formula types (held, not blocked).
		if (check && check.state === 'ok') return { op: undefined, result: 'number', axis: 'number', mixed: false, hidden: false };
		if (heldCheck(check)) return { op: undefined, ...answerOfType(check.type), hidden: false };
		return { op: undefined, result: null, axis: null, mixed: false, hidden: false };
	}
	if (isPerItemMeasure(m)) {
		if (!check || (check.state !== 'ok' && !heldCheck(check)) || !check.description) return { op: m.op, result: null, axis: null, mixed: false, hidden };
		return { op: m.op, ...measureAnswer(m.op, check.description), hidden };
	}
	if (m.op === 'count') return { op: 'count', ...measureAnswer('count', {}), hidden };
	const field = fieldNamed(ctx, m.element);
	if (!field) return { op: m.op, result: null, axis: null, mixed: false, hidden };
	const options = (field.element && field.element.options) || {};
	const unit = m.unit || (field.description.kind === 'measurement' ? options.defaultUnit : undefined);
	let answer = measureAnswer(m.op, field.description, { unit });
	// Shown in the currency asked for, it is on that currency's axis alone (A2b).
	if (m.currency && answer.result === 'money') answer = { result: 'money', axis: `money:${m.currency}`, mixed: false };
	return { op: m.op, ...answer, hidden };
}

/** A formula the run refuses (`not_offered_yet`) whose result the module could still type. */
const heldCheck = (check) => Boolean(check) && check.state === 'refused' && check.code === 'not_offered_yet' && Boolean(check.type);

/** What a formula across measures answers, from its result type (keepr-api spec.js validateFormulaMeasure). */
function answerOfType(t) {
	if (t && t.type === 'money') {
		const one = Array.isArray(t.currencies) && t.currencies.length === 1 ? t.currencies[0] : (t.currency || null);
		return one ? { result: 'money', axis: `money:${one}`, mixed: false } : { result: 'money', axis: 'money:*', mixed: true };
	}
	if (t && t.type === 'measure') return t.measure ? { result: 'measure', axis: `measure:${t.measure}`, mixed: false } : { result: 'measure', axis: 'measure:*', mixed: true };
	return { result: 'number', axis: 'number', mixed: false };
}

/** The currency a money measure is shown in: the one asked for, else the element's default. */
export function measureCurrency(m, ctx) {
	if (!m || m.keep || m.op === 'count') return null;
	const field = fieldNamed(ctx, m.element);
	if (!field || field.description.kind !== 'money') return null;
	return m.currency || defaultCodeOf((field.element && field.element.options) || {}) || null;
}

/** The unit a measurement measure is shown in: the one asked for, else the element's default. */
export function measureUnit(m, ctx) {
	if (!m || m.keep || m.op === 'count') return null;
	const field = fieldNamed(ctx, m.element);
	if (!field || field.description.kind !== 'measurement') return null;
	const options = (field.element && field.element.options) || {};
	return m.unit || options.defaultUnit || null;
}

// The words a per-item measure goes by when the author gave it none
// (keepr-api analytics/formulas.js perItemLabel).
const PER_ITEM_WORDS = Object.freeze({
	countNonBlank: 'Count of', distinct: 'Distinct values of', sum: 'Total of', avg: 'Average of', median: 'Median of',
	latest: 'Latest value of', countYes: 'Count of Yes in', shareYes: 'Share of Yes in',
});

/** "Total of the calculation" — or, on a chart of several per-item measures, "Total of calculation 2". */
function perItemDefaultLabel(op, check, ordinal = 1, of = 1) {
	const which = of > 1 ? `calculation ${ordinal}` : 'the calculation';
	const dated = Boolean(check && check.state === 'ok' && has(DATED_KINDS, check.type.type));
	if (op === 'min') return `${dated ? 'Earliest' : 'Smallest'} of ${which}`;
	if (op === 'max') return `${dated ? 'Latest' : 'Largest'} of ${which}`;
	return own(PER_ITEM_WORDS, op) ? `${PER_ITEM_WORDS[op]} ${which}` : `The ${which.replace(/^the /, '')}`;
}

const LABEL_MAX = LIMITS.labelLength;
const clip = (text) => (text.length > LABEL_MAX ? `${text.slice(0, LABEL_MAX - 1)}…` : text);

/** A formula across measures in words: each measure it names by its label ("Total of Amount / Count items"). */
function formulaWords(text, names) {
	const words = String(text || '').replace(/\{\{([^{}]*)\}\}/g, (whole, inner) => {
		const key = inner.trim();
		return names.has(key) ? names.get(key) : key;
	}).replace(/\s+/g, ' ').trim();
	return words ? clip(words) : '';
}

/** A kept calculation read in words: each `{{name}}` becomes the label `labelOf` gives it (the name itself when nothing does), never clipped. */
function keptFormulaWords(text, labelOf) {
	return String(text || '').replace(/\{\{([^{}]*)\}\}/g, (whole, inner) => labelOf(inner.trim()) || inner.trim()).replace(/\s+/g, ' ').trim();
}

// What each measure's formula is worth, worked out once per draft and world
// (the steps, the spec and the forms all ask): a WeakMap on the measures
// array, then on ctx — both are replaced, never changed in place.
const CHECKS = new WeakMap();
const NO_CTX = Object.freeze({});

/**
 * Every measure's formula checked (formulaModel: `checks[i]`, null for a
 * measure with none), each measure's default label (`defaults`, the words
 * the server would give it — or, for a formula across measures, the words
 * the builder sends) and its name (`names`: its own label, else the
 * default), by key. A formula across measures reads the measures above it,
 * each as what it answers.
 */
export function formulaChecks(draft, ctx) {
	const measures = (draft && Array.isArray(draft.measures)) ? draft.measures : [];
	const world = ctx || NO_CTX;
	let byCtx = CHECKS.get(measures);
	if (!byCtx) {
		byCtx = new WeakMap();
		CHECKS.set(measures, byCtx);
	}
	if (byCtx.has(world)) return byCtx.get(world);
	const isPerItemLike = (m) => isPerItemMeasure(m) || Boolean(m && m.keep && m.keep.perItem !== undefined);
	const of = measures.filter(isPerItemLike).length;
	const checks = measures.map((m) => (isPerItemMeasure(m) ? checkPerItem(m.perItem, world) : null));
	const defaults = new Map();
	const names = new Map();
	const typed = (m) => (m && typeof m.label === 'string' ? m.label.trim() : '');
	let ordinal = 0;
	measures.forEach((m, i) => {
		if (!m) return;
		if (isPerItemLike(m)) ordinal += 1;
		if (isFormulaMeasure(m)) return;
		const label = isPerItemMeasure(m) ? perItemDefaultLabel(m.op, checks[i], ordinal, of) : elementDefaultLabel(m, world);
		defaults.set(m.key, label);
		names.set(m.key, typed(m) || label);
	});
	const above = new Map();
	measures.forEach((m, i) => {
		if (!m) return;
		if (isFormulaMeasure(m)) {
			const below = new Map(measures.slice(i + 1).filter(Boolean).map((x) => [x.key, names.get(x.key) || typed(x) || x.key]));
			checks[i] = checkAcross(m.formula, above, below);
			const words = checks[i].state === 'ok' ? formulaWords(checks[i].canonical, names) : formulaWords(m.formula, names);
			const label = m.keyLabel ? m.key : (words || 'Formula');
			defaults.set(m.key, label);
			names.set(m.key, typed(m) || label);
		}
		above.set(m.key, aboveEntry(m, checks[i], world, names.get(m.key) || m.key));
	});
	const out = { checks, defaults, names };
	byCtx.set(world, out);
	return out;
}

/** What a measure answers to the formulas below it, and whether it is finished. */
function aboveEntry(m, check, ctx, label) {
	if (m.keep) {
		const keep = m.keep;
		const field = typeof keep.element === 'string' ? fieldNamed(ctx, keep.element) : null;
		if (field && typeof keep.op === 'string') return { answer: measureAnswer(keep.op, field.description), description: field.description, label, ready: true };
		return { answer: { result: 'number' }, description: null, label, ready: true };
	}
	if (isFormulaMeasure(m)) {
		if (heldCheck(check)) return { answer: answerOfType(check.type), description: null, label, ready: true };
		return { answer: { result: 'number' }, description: null, label, ready: Boolean(check && check.state === 'ok') };
	}
	if (isPerItemMeasure(m)) {
		const ok = Boolean(check && (check.state === 'ok' || heldCheck(check)) && check.description) && has(check.description.measures, m.op);
		return { answer: ok ? measureAnswer(m.op, check.description) : null, description: ok ? check.description : null, label, ready: ok };
	}
	if (m.op === 'count') return { answer: measureAnswer('count', {}), description: null, label, ready: true };
	const field = fieldNamed(ctx, m.element);
	const ok = Boolean(field) && has(field.description.measures, m.op);
	return { answer: ok ? answerOf(m, ctx) : null, description: ok ? field.description : null, label, ready: ok };
}

/** The keys a formula across measures names, as written — read whether or not it parses yet. */
function keysReadByFormulas(draft) {
	const read = new Set();
	(draft.measures || []).forEach((m) => {
		if (isFormulaMeasure(m)) namesIn(m.formula).forEach((key) => read.add(key));
		else if (m && m.keep && typeof m.keep.formula === 'string') namesIn(m.keep.formula).forEach((key) => read.add(key));
	});
	return read;
}

/** The words an element measure (or Count items, or a kept one) goes by with no label of its own. */
function elementDefaultLabel(m, ctx) {
	if (!m) return '';
	if (m.keep) return (m.keep && typeof m.keep.label === 'string' && m.keep.label) || m.key || 'Calculation';
	if (m.op === 'count') return countWords(ctx);
	const field = fieldNamed(ctx, m.element);
	const e = field ? field.label : (m.element ? fieldWords(m.element) : '…');
	const dated = isDated(field);
	switch (m.op) {
		case 'countNonBlank': return `Count of ${e}`;
		case 'distinct': return `Distinct ${e}`;
		case 'sum': return `Total of ${e}`;
		case 'avg': return `Average of ${e}`;
		case 'median': return `Median of ${e}`;
		case 'min': return dated ? `Earliest ${e}` : `Smallest ${e}`;
		case 'max': return dated ? `Latest ${e}` : `Largest ${e}`;
		case 'latest': return `Latest ${e}`;
		case 'countYes': return `Count of Yes in ${e}`;
		case 'shareYes': return `Share of Yes in ${e}`;
		default: return e;
	}
}

/**
 * The words the server gives a measure with no label of its own (keepr-api
 * analytics/plan.js measureLabel, formulas.js perItemLabel). Given the draft,
 * a per-item calculation counts among the chart's others ("Total of
 * calculation 2") and a formula across measures reads as its formula in words.
 */
export function defaultMeasureLabel(m, ctx, draft = null) {
	if (!m) return '';
	if (draft && Array.isArray(draft.measures) && draft.measures.indexOf(m) !== -1) {
		const { defaults } = formulaChecks(draft, ctx);
		if (defaults.has(m.key)) return defaults.get(m.key);
	}
	if (isFormulaMeasure(m)) return m.keyLabel ? m.key : 'Formula';
	if (isPerItemMeasure(m)) return perItemDefaultLabel(m.op, checkPerItem(m.perItem, ctx || NO_CTX));
	return elementDefaultLabel(m, ctx);
}

const measureName = (m, ctx, draft = null) => (m && typeof m.label === 'string' && m.label.trim()) || defaultMeasureLabel(m, ctx, draft);

// --------------------------------------------------------------- shapes

/** How many of its own values an axis holds before a run (keepr-api spec.js axisValues). */
function axisValues(field, group, draft) {
	const g = groupOfField(field);
	if (!g || !group) return null;
	if (has(CYCLIC_BUCKETS, group.bucket)) return estimateBuckets(null, group.bucket);
	if (g.kind === 'boolean') return 2;
	if (g.kind === 'rating') return typeof g.values === 'number' ? g.values + 1 : null;
	if (g.kind === 'choice' && field.element) {
		// A Choice's list only (KPE-033); the capability table says `choice` for nothing else.
		return choiceListOf(field.element).length;
	}
	if (g.kind === 'clock') return estimateBuckets(null, group.bucket);
	if (g.kind === 'bins' && group.binsMode === 'count' && typeof group.binsCount === 'number') return group.binsCount;
	if (g.kind === 'time' && draft.time && draft.time.on === group.on) return estimateBuckets(draft.time.range, group.bucket);
	return null;
}

function axisCount(field, group, draft) {
	const g = groupOfField(field);
	const values = axisValues(field, group, draft);
	if (values === null && !(g && g.top)) return undefined;
	const top = typeof group.top === 'number' ? group.top : (g && g.top ? g.top.default || g.top.max : undefined);
	const count = drawnCount({ values, top, keep: Number.MAX_SAFE_INTEGER, blanks: group.blanks });
	return count === null ? undefined : count;
}

/** The shape formsForShape reads, from the draft. */
export function shapeOf(draft, ctx) {
	const { checks } = formulaChecks(draft, ctx);
	const measures = (draft.measures || []).map((m, i) => answerOf(m, ctx, checks[i]));
	const groupField = draft.groupBy ? fieldNamed(ctx, draft.groupBy.on) : null;
	const splitField = draft.splitBy ? fieldNamed(ctx, draft.splitBy.on) : null;
	const groupShape = (field, group) => (field && groupOfField(field)
		? { axis: axisOf(groupOfField(field), group.bucket), bucket: group.bucket || undefined, count: axisCount(field, group, draft) }
		: null);
	return {
		measures,
		group: draft.groupBy ? groupShape(groupField, draft.groupBy) : null,
		split: draft.splitBy && draft.groupBy ? groupShape(splitField, draft.splitBy) : null,
	};
}

/** Two drawn measures of one measure shown in different units share a table only (A2b: one axis shows one unit). */
function unitClash(draft, ctx) {
	const seen = new Map();
	return (draft.measures || []).some((m) => {
		if (!m || m.keep || m.op === 'count') return false;
		const field = fieldNamed(ctx, m.element);
		if (!field || field.description.kind !== 'measurement' || !field.description.measure || has(field.description.notes, 'perUnit')) return false;
		const answer = answerOf(m, ctx);
		if (answer.result !== 'measure') return false;
		const unit = measureUnit(m, ctx) || '';
		const prior = seen.get(field.description.measure);
		if (prior === undefined) { seen.set(field.description.measure, unit); return false; }
		return prior !== unit;
	});
}

/** The forms this question may be drawn as, the recommended one first (the vendored formsForShape). */
export function formsFor(draft, ctx) {
	const shape = shapeOf(draft, ctx);
	if (shape.measures.some((m) => !m.keep && m.op !== 'count' && m.result === null)) return [];
	const forms = formsForShape(shape);
	if (unitClash(draft, ctx)) return forms.filter((f) => f === 'table');
	// A calculation the builder keeps (a per-item formula, a formula across
	// measures) answers what only its formula says: offer the form the chart
	// was saved with, and the table — never a form the server would refuse
	// for an answer the builder cannot see (the A5 review's L-1).
	if ((draft.measures || []).some((m) => m && m.keep)) {
		const allowed = [draft.show.savedType, 'table'].filter(Boolean);
		const kept = forms.filter((f) => has(allowed, f));
		return kept.length ? kept : ['table'];
	}
	return forms;
}

// ---------------------------------------------------------------- goals

/** What a progress goal is typed in: a share as a percent, money in its major units, a measurement in its unit. */
export function goalUnits(draft, ctx) {
	const visible = (draft.measures || []).filter((m) => m && !(m.keep && m.keep.hidden) && m.hidden !== true);
	const m = visible[0];
	if (!m) return { kind: 'number' };
	if (m.op === 'shareYes') return { kind: 'share', suffix: '%' };
	const answer = answerOf(m, ctx, formulaChecks(draft, ctx).checks[draft.measures.indexOf(m)]);
	if (answer.result === 'money') {
		const code = measureCurrency(m, ctx);
		const exponent = code ? exponentOf(code) : null;
		return { kind: 'money', code, exponent: typeof exponent === 'number' ? exponent : 2, suffix: code || '' };
	}
	if (answer.result === 'measure') {
		const unit = measureUnit(m, ctx);
		const known = unit ? getUnit(unit) : null;
		return { kind: 'measure', unit, suffix: known ? known.symbol || known.id : unit || '' };
	}
	return { kind: 'number' };
}

const tidy = (n) => Number(Number(n).toPrecision(12));

/** The goal the person typed, in the run's own units (a share 0–1, money in minor units). */
export function goalToSpec(goal, units) {
	if (typeof goal !== 'number' || !Number.isFinite(goal)) return undefined;
	if (units.kind === 'share') return tidy(goal / 100);
	if (units.kind === 'money') return Math.round(goal * (10 ** units.exponent));
	return goal;
}

/** A stored goal in the units a person types. */
export function goalFromSpec(goal, units) {
	if (typeof goal !== 'number' || !Number.isFinite(goal)) return null;
	if (units.kind === 'share') return tidy(goal * 100);
	if (units.kind === 'money') return tidy(goal / (10 ** units.exponent));
	return goal;
}

/** A draft from draftFromSpec, with its goal turned into the person's units now that the fields are known. */
export function settleGoal(draft, ctx) {
	if (!draft || !draft.show || !draft.show.goalRaw) return draft;
	const show = { ...draft.show, goal: goalFromSpec(draft.show.goal, goalUnits(draft, ctx)) };
	delete show.goalRaw;
	return { ...draft, show };
}

// ---------------------------------------------------------- availability

/**
 * May this chart show the previous period beside its own? Number and line
 * only, over a range that is not all time, grouped only by the chart's own
 * date and never split by a date (keepr-api spec.js compare_not_allowed).
 * `{ offered, reason }` — offered false with a reason once the form allows it
 * but the question does not; `shown` false where the form never does.
 */
function compareRule(draft, ctx) {
	if (!has(COMPARE_FORMS, draft.show.type)) return { shown: false, offered: false, reason: null };
	const timeField = fieldNamed(ctx, draft.time.on);
	if (!timeField) return { shown: true, offered: false, reason: 'Choose the chart\'s date under Time to compare with the previous period.' };
	if (!draft.time.range) return { shown: true, offered: false, reason: 'All time has no previous period to compare with. Choose a range.' };
	const groupField = draft.groupBy ? fieldNamed(ctx, draft.groupBy.on) : null;
	const groupKind = groupOfField(groupField) && groupOfField(groupField).kind;
	if (groupKind === 'time' && draft.groupBy.on !== draft.time.on) {
		return { shown: true, offered: false, reason: `A comparison lines up the chart's own dates (${timeField.label}). Group by them, or leave the comparison off.` };
	}
	const splitField = draft.splitBy ? fieldNamed(ctx, draft.splitBy.on) : null;
	if (groupOfField(splitField) && groupOfField(splitField).kind === 'time') {
		return { shown: true, offered: false, reason: 'A comparison can\'t line up a split by dates. Remove the split to compare.' };
	}
	return { shown: true, offered: true, reason: null };
}

/** The drawn measures that do not add up, by name: stacks and running totals need totals or counts (A2b's R4). */
function nonAdding(draft, ctx) {
	return (draft.measures || [])
		.filter((m) => {
			if (!m) return false;
			if (m.keep) {
				// Not drawn, so on no stack or running total.
				if (m.keep.hidden === true) return false;
				// A formula across measures: whether it adds up is its formula's
				// (a ratio does not), so it is never stacked or run up here.
				if (m.keep.formula !== undefined) return true;
				return !has(PART_OF_WHOLE_OPS, m.keep.op);
			}
			if (m.hidden === true) return false;
			// A formula across measures has no op (null), so it is never a count
			// or a total and never adds up (keepr-api spec.js option_needs_totals).
			return !has(PART_OF_WHOLE_OPS, m.op);
		})
		.map((m) => measureName(m, ctx, draft));
}

function visibleMeasureCount(draft) {
	return (draft.measures || []).filter((m) => m && !(m.keep && m.keep.hidden) && m.hidden !== true).length;
}

/** Which of the chosen form's options this question takes, each with why not when it can't. */
export function formOptionRules(draft, ctx) {
	const type = draft.show.type;
	const allowed = own(FORM_OPTIONS, type) ? FORM_OPTIONS[type] : [];
	const rules = {};
	const hasTime = Boolean(fieldNamed(ctx, draft.time.on));
	const several = Boolean(draft.splitBy) || visibleMeasureCount(draft) > 1;
	const adds = nonAdding(draft, ctx);
	if (has(allowed, 'lowerIsBetter')) rules.lowerIsBetter = { offered: true };
	if (has(allowed, 'sparkline')) rules.sparkline = hasTime ? { offered: true } : { offered: false, reason: 'Choose the chart\'s date under Time to draw a sparkline.' };
	if (has(allowed, 'goal')) rules.goal = { offered: true };
	if (has(allowed, 'orientation')) rules.orientation = { offered: true };
	if (has(allowed, 'stack') && several) {
		rules.stack = adds.length
			? { offered: false, reason: `${adds[0]} doesn't add up, so it can't be stacked. Chart a total or a count to stack.` }
			: { offered: true };
	}
	if (has(allowed, 'sort')) rules.sort = { offered: true };
	if (has(allowed, 'cumulative')) {
		rules.cumulative = adds.length
			? { offered: false, reason: `${adds[0]} doesn't add up, so it can't be a running total. Chart a total or a count instead.` }
			: { offered: true };
	}
	if (has(allowed, 'markers')) rules.markers = { offered: true };
	const compare = compareRule(draft, ctx);
	if (compare.shown) rules.compare = { offered: compare.offered, reason: compare.reason };
	return rules;
}

// ------------------------------------------------------------- the spec

function groupToSpec(group, field, ctx) {
	const g = groupOfField(field);
	const out = { on: specRef(ctx, group.on) };
	if (g && Array.isArray(g.buckets) && group.bucket) out.bucket = group.bucket;
	if (g && g.kind === 'bins') {
		// The server refuses ranges with no size (bins_required); ten equal
		// ranges (the table's DEFAULT_BINS, what defaultChartFor proposes)
		// until the person says otherwise.
		if (group.binsMode === 'size') {
			if (typeof group.binsSize === 'number') out.bins = { size: group.binsSize };
		} else out.bins = { count: typeof group.binsCount === 'number' ? group.binsCount : DEFAULT_BINS };
	}
	if (g && g.top && typeof group.top === 'number') out.top = group.top;
	if (field && field.description && Array.isArray(field.description.multi)) out.multi = group.multi || 'earliest';
	out.blanks = group.blanks === 'hide' ? 'hide' : 'show';
	return out;
}

/**
 * The spec this draft asks, as keepr-api's validator reads it (SCHEMA
 * "Charts" › the spec): only the keys that apply, every option the chosen
 * form does not take left out (the server refuses one, `option_not_for_form`,
 * even when false), a group by ranges always with its `bins` (the server has
 * no default, `bins_required`), the goal in the run's own units. A formula
 * goes as typed (the server stores it canonical); a per-item calculation's
 * "Count as zero" only where its result has a zero; `hidden` only on a
 * measure a formula reads (d36); a formula across measures the author did
 * not label carries its formula in words, which is what the builder shows
 * (the server would call it by its key).
 */
export function specFromDraft(draft, ctx) {
	const spec = {
		v: 1,
		card_id: draft.cardId || null,
		subCollections: draft.subCollections !== false,
		filter: typeof draft.filter === 'string' ? draft.filter : '',
		time: null,
		measures: [],
		groupBy: null,
		splitBy: null,
		show: { type: draft.show.type },
	};
	const timeField = fieldNamed(ctx, draft.time.on);
	const rules = formOptionRules(draft, ctx);
	// Counting a group's entries (KPR-684): the group, as a field names an element.
	const counted = draft.entries ? countedGroup(ctx) : null;
	if (counted) spec.entries = fieldRefFor(counted) || counted.name;
	if (draft.time.on) {
		spec.time = { on: specRef(ctx, draft.time.on), range: draft.time.range || null, compare: rules.compare && rules.compare.offered && draft.time.compare ? 'previous' : null };
		if (timeField && Array.isArray(timeField.description.multi)) spec.time.multi = draft.time.multi || 'earliest';
	}
	const { checks, defaults } = formulaChecks(draft, ctx);
	const read = keysReadByFormulas(draft);
	spec.measures = (draft.measures || []).map((m, i) => {
		if (m.keep) return m.keep;
		const label = typeof m.label === 'string' ? m.label.trim() : '';
		if (isFormulaMeasure(m)) {
			const out = { key: m.key, formula: m.formula };
			const named = label || (m.keyLabel ? '' : defaults.get(m.key) || '');
			if (named) out.label = named;
			return out;
		}
		const out = { key: m.key, op: m.op };
		if (isPerItemMeasure(m)) {
			out.perItem = m.perItem;
			const check = checks[i];
			if (check && check.state === 'ok') {
				// "Count as zero" means something only for a result with a zero
				// (keepr-api spec.js blanks_not_applicable): sent where it applies.
				const zeroable = check.description.zeroable && m.op !== 'countNonBlank' && m.op !== 'distinct';
				out.blanks = zeroable && m.blanks === 'zero' ? 'zero' : 'omit';
			} else out.blanks = m.blanks === 'zero' ? 'zero' : 'omit';
		} else if (m.op !== 'count') {
			if (m.element) out.element = specRef(ctx, m.element);
			out.blanks = m.blanks === 'zero' ? 'zero' : 'omit';
			const field = fieldNamed(ctx, m.element);
			if (m.unit && field && field.description.kind === 'measurement') out.unit = m.unit;
			if (m.currency && field && field.description.kind === 'money') out.currency = m.currency;
		}
		if (label) out.label = label;
		if (m.hidden === true && read.has(m.key)) out.hidden = true;
		return out;
	});
	if (draft.groupBy) spec.groupBy = groupToSpec(draft.groupBy, fieldNamed(ctx, draft.groupBy.on), ctx);
	if (draft.groupBy && draft.splitBy) spec.splitBy = groupToSpec(draft.splitBy, fieldNamed(ctx, draft.splitBy.on), ctx);
	const show = draft.show;
	if (rules.lowerIsBetter && show.lowerIsBetter) spec.show.lowerIsBetter = true;
	if (rules.sparkline && rules.sparkline.offered && show.sparkline) spec.show.sparkline = true;
	if (rules.goal) {
		const goal = goalToSpec(show.goal, goalUnits(draft, ctx));
		if (goal !== undefined) spec.show.goal = goal;
	}
	if (rules.orientation && show.orientation && show.orientation !== 'auto') spec.show.orientation = show.orientation;
	if (rules.stack && rules.stack.offered && show.stack && show.stack !== 'none') spec.show.stack = show.stack;
	if (rules.sort && show.sort && show.sort !== 'group') spec.show.sort = show.sort;
	if (rules.cumulative && rules.cumulative.offered && show.cumulative) spec.show.cumulative = true;
	if (rules.markers && show.markers) spec.show.markers = true;
	return spec;
}

// ----------------------------------------------------------- reconciling

function bucketFits(draft, group, bucket) {
	if (!draft.time || draft.time.on !== group.on) return true;
	const n = estimateBuckets(draft.time.range, bucket);
	return n === null || n <= LIMITS.buckets;
}

/**
 * A bucket for a split on the group's own date other than the group's: the
 * coarsest that differs and fits (grouped by month, split by year), or null
 * for a field with no buckets.
 */
function otherBucket(draft, ctx) {
	const g = groupOfField(fieldNamed(ctx, draft.splitBy.on));
	if (!g || !Array.isArray(g.buckets)) return null;
	const linear = g.buckets.filter((b) => !has(CYCLIC_BUCKETS, b));
	return linear.slice().reverse().find((b) => b !== draft.groupBy.bucket && bucketFits(draft, draft.splitBy, b)) || null;
}

/** The finest bucket of a time group that fits the chart's range, or null. */
function finestFitting(draft, group, g) {
	const linear = (g.buckets || []).filter((b) => !has(CYCLIC_BUCKETS, b));
	return linear.find((b) => bucketFits(draft, group, b)) || null;
}

function reconcileGroup(draft, ctx, which, notices) {
	const group = draft[which];
	if (!group) return null;
	const step = which;
	const field = fieldNamed(ctx, group.on);
	const word = which === 'groupBy' ? 'Group by' : 'Split by';
	if (!field) {
		const other = missingFieldWords(ctx, group.on);
		notices.push({ step, text: other ? `${word} was cleared: ${other}` : `${word} was cleared: ${ctx.cardName ? `the ${ctx.cardName} card` : 'this chart'} has no ${fieldWords(group.on)}.` });
		return null;
	}
	if (!isGroupable(field)) {
		notices.push({ step, text: `${word} was cleared: a chart can't group by ${field.label}.` });
		return null;
	}
	const g = groupOfField(field);
	const next = { ...group };
	if (Array.isArray(g.buckets)) {
		if (!has(g.buckets, next.bucket)) next.bucket = g.defaultBucket || g.buckets[0] || null;
		if (!bucketFits(draft, next, next.bucket)) {
			const finer = next.bucket;
			const fit = finestFitting(draft, next, g);
			if (fit && fit !== finer) {
				next.bucket = fit;
				const n = estimateBuckets(draft.time.range, finer);
				notices.push({ step, text: `Grouped by ${bucketPhrase(fit)} now: by ${bucketPhrase(finer)}, ${rangeLabel(draft.time.range).toLowerCase()} could be ${n.toLocaleString('en-US')} bars, more than the ${LIMITS.buckets} a chart draws.` });
			}
		}
	} else next.bucket = null;
	if (g.kind !== 'bins') { next.binsCount = null; next.binsSize = null; next.binsMode = 'count'; } else if (next.binsMode !== 'size' && typeof next.binsCount !== 'number') next.binsCount = DEFAULT_BINS;
	if (!g.top) next.top = null;
	else if (typeof next.top === 'number') {
		const max = which === 'splitBy' ? Math.min(g.top.max, LIMITS.series) : g.top.max;
		if (next.top > max) next.top = max;
	}
	next.multi = Array.isArray(field.description.multi) ? (has(field.description.multi, next.multi) ? next.multi : 'earliest') : null;
	return next;
}

function reconcileMeasures(draft, ctx, notices) {
	const { checks } = formulaChecks(draft, ctx);
	const read = keysReadByFormulas(draft);
	return (draft.measures || []).map((original, i) => {
		if (!original || original.keep) return original;
		let m = original;
		// Hidden only while a formula reads it (keepr-api spec.js hidden_unused,
		// d36). The person's choice (`hideWish`) is kept: a pill taken out of a
		// formula mid-edit shows the measure, and putting it back hides it
		// again — nothing changes under the typist, and nothing is announced.
		const wish = m.hideWish === undefined ? m.hidden === true : m.hideWish === true;
		const hidden = wish && read.has(m.key);
		if (m.hidden !== hidden || m.hideWish !== wish) m = { ...m, hidden, hideWish: wish };
		if (isFormulaMeasure(m)) return m;
		if (isPerItemMeasure(m)) {
			// The verb follows the result's own default until the person picks
			// one; a verb they picked that the result can't take is said on the
			// verb (incompleteOf), never changed under them.
			const check = checks[i];
			const fallback = check && check.state === 'ok' ? check.description.defaultMeasure : null;
			if (!m.opChosen && fallback && m.op !== fallback) return { ...m, op: fallback };
			return m;
		}
		if (m.op === 'count') return m;
		const next = { ...m };
		if (next.element) {
			const field = fieldNamed(ctx, next.element);
			if (!field) {
				const other = missingFieldWords(ctx, m.element);
				notices.push({ step: 'measures', text: other ? `${defaultMeasureLabel(m, ctx)} was cleared: ${other}` : `${defaultMeasureLabel(m, ctx)} was cleared: ${ctx.cardName ? `the ${ctx.cardName} card` : 'this chart'} has no ${fieldWords(m.element)}.` });
				next.element = null;
			} else if (!has(field.description.measures, next.op)) {
				notices.push({ step: 'measures', text: `${field.entriesReason || refusalFor(next.op, field.description, columnWords(field))} It was cleared from the measure.` });
				next.element = null;
			}
		}
		const field = fieldNamed(ctx, next.element);
		if (next.blanks === 'zero' && (!field || !field.description.zeroable || next.op === 'countNonBlank' || next.op === 'distinct')) {
			if (field) notices.push({ step: 'measures', text: `Items with no ${field.label} are left out again: an empty ${field.label} can't count as zero here.` });
			next.blanks = 'omit';
		}
		if (next.unit && (!field || field.description.kind !== 'measurement' || !isUnitOf(next.unit, field.description.measure))) next.unit = null;
		if (next.currency && (!field || field.description.kind !== 'money')) next.currency = null;
		return next;
	});
}

/**
 * Clears whatever a later change made impossible — a measure's element the
 * new card lacks, a group the new card cannot draw, a bucket the range would
 * cut into more than 400 bars, a comparison the question no longer allows, a
 * form the shape no longer takes — and says each in one line, never leaving
 * a choice dangling (plan §6.2). A form the person never chose follows the
 * recommendation as the question changes; one they chose is kept while it
 * fits. Returns `{ draft, notices: [{ step, text }] }`; a draft with nothing
 * to clear comes back as the same object.
 */
export function reconcile(draft, ctx) {
	const notices = [];
	let next = { ...draft };
	// Counting a group's entries (KPR-684): a group this card holds, else items.
	if (next.entries && ctx && Array.isArray(ctx.elements) && !countedGroup({ ...ctx, entries: next.entries })) {
		notices.push({ step: 'items', text: `The chart counts items now: ${ctx.cardName ? `the ${ctx.cardName} card` : 'this chart'} has no ${fieldWords(next.entries)} to count the entries of.` });
		next.entries = null;
	}
	next.measures = reconcileMeasures(next, ctx, notices);
	next.groupBy = reconcileGroup(next, ctx, 'groupBy', notices);
	next.splitBy = next.groupBy ? reconcileGroup(next, ctx, 'splitBy', notices) : null;
	if (draft.splitBy && !next.groupBy) {
		notices.push({ step: 'splitBy', text: 'Split by was cleared: it needs a Group by.' });
	}
	if (next.groupBy && next.splitBy && next.splitBy.on === next.groupBy.on && next.splitBy.bucket === next.groupBy.bucket) {
		// A date split by itself in another bucket is a real question (grouped
		// by month, split by year: year over year), and the server takes it
		// (split_same_as_group needs the same bucket too): give the split a
		// bucket of its own rather than clear it (the A5 review's L-2).
		const other = otherBucket(next, ctx);
		if (other) next.splitBy = { ...next.splitBy, bucket: other };
		else {
			notices.push({ step: 'splitBy', text: 'Split by was cleared: it was the same as Group by.' });
			next.splitBy = null;
		}
	}
	// The chart's date.
	const time = { ...next.time };
	if (time.on) {
		const field = fieldNamed(ctx, time.on);
		if (!isTimeField(field)) {
			// A column is the chart's date only while the chart counts its group's entries (keepr-api KPR-806).
			const counting = countedGroup({ ...ctx, entries: next.entries });
			const col = !field && columnRefOf(time.on) ? columnForField(ctx.elements, time.on, { shadowed: ctx.shadowed }) : null;
			let text = `The chart's date was cleared: ${ctx.cardName ? `the ${ctx.cardName} card` : 'this chart'} has no ${field ? field.label : fieldWords(time.on)} date to use.`;
			if (field && field.column && !counting) text = `The chart's date was cleared: ${columnWords(field)} belongs to each ${elementLabel(field.group) || field.group.name} entry, and the chart counts items now.`;
			else if (col && counting && counting.name !== col.group.name) text = `The chart's date was cleared: ${elementLabel(col.column) || col.column.name} is a column of ${elementLabel(col.group) || col.group.name}, and this chart counts ${elementLabel(counting) || counting.name} entries.`;
			notices.push({ step: 'time', text });
			time.on = null;
			time.multi = null;
			time.compare = false;
		} else {
			time.multi = Array.isArray(field.description.multi) ? (has(field.description.multi, time.multi) ? time.multi : 'earliest') : null;
		}
	} else {
		time.multi = null;
	}
	next.time = time;
	// The form: the recommended one until the person picks; theirs while it fits.
	const forms = formsFor(next, ctx);
	if (forms.length) {
		if (!next.show.chosen) {
			if (next.show.type !== forms[0]) next.show = { ...next.show, type: forms[0] };
		} else if (!has(forms, next.show.type)) {
			notices.push({ step: 'show', text: `Shown as ${articled(chartFormWord(forms[0]).toLowerCase())} now: ${articled(chartFormWord(next.show.type).toLowerCase())} can't show this question.` });
			next.show = { ...next.show, type: forms[0], chosen: false };
		}
	}
	// The comparison, once the form and the groups are settled.
	if (next.time.compare) {
		const rule = compareRule(next, ctx);
		if (!rule.offered) {
			if (rule.shown) notices.push({ step: 'show', text: `The comparison with the previous period is off: ${lowerFirst(rule.reason)}` });
			next.time = { ...next.time, compare: false };
		}
	}
	if (!notices.length && sameDraft(draft, next)) return { draft, notices };
	return { draft: next, notices };
}

/**
 * Removes the measure at `index`. A formula across measures that read it is
 * cleared, never left naming a measure that is gone (plan §6.2), and says so
 * in one line; one the builder keeps as it came goes too. The chart keeps at
 * least one measure. Returns `{ draft, notices }`.
 */
export function removeMeasure(draft, index, ctx) {
	const target = draft && Array.isArray(draft.measures) ? draft.measures[index] : null;
	if (!target) return { draft, notices: [] };
	const { names } = formulaChecks(draft, ctx);
	const gone = names.get(target.key) || target.key;
	const notices = [];
	const measures = [];
	draft.measures.forEach((m, i) => {
		if (i === index) return;
		if (isFormulaMeasure(m) && namesIn(m.formula).has(target.key)) {
			// Named by its own label; an unlabelled one's words would name the measure that went.
			const label = typeof m.label === 'string' ? m.label.trim() : '';
			notices.push({ step: 'measures', text: label ? `${label} was cleared: its formula read ${gone}, which was removed.` : `The formula that read ${gone} was cleared: ${gone} was removed.` });
			measures.push({ ...m, formula: '' });
			return;
		}
		if (m && m.keep && typeof m.keep.formula === 'string' && namesIn(m.keep.formula).has(target.key)) {
			notices.push({ step: 'measures', text: `${names.get(m.key) || m.key} was removed too: its formula read ${gone}.` });
			return;
		}
		measures.push(m);
	});
	return { draft: { ...draft, measures: measures.length ? measures : [newMeasure([])] }, notices };
}

/**
 * A card switch (ctx is the new card's world, `previous` the old one's): a per-item calculation that
 * reads an element the new card lacks is cleared, with one line, as an
 * element measure's element is (reconcile). Only on a switch — a formula
 * being typed is never cleared under the typist; it says what is wrong
 * instead. On a chart of every card the calculation stays, refused in words.
 */
export function clearFormulasForCard(draft, ctx, previous = null) {
	if (!draft || !ctx || !ctx.card) return { draft, notices: [] };
	const notices = [];
	const where = ctx.cardName ? `the ${ctx.cardName} card` : 'this card';
	// The element is named as the person knows it: its label on the card it was on.
	const before = new Map(formulaElements(previous).map((el) => [el.name, el]));
	const labelOf = (name) => (before.has(name) ? elementLabel(before.get(name)) || name : name);
	const measures = (draft.measures || []).map((m) => {
		if (!isPerItemMeasure(m) || !m.perItem.trim()) return m;
		const missing = missingNames(m.perItem, ctx);
		if (!missing.length) return m;
		notices.push({ step: 'measures', text: `${measureName(m, ctx, draft)} was cleared: ${where} has no ${labelOf(missing[0])}.` });
		return { ...m, perItem: '' };
	});
	return notices.length ? { draft: { ...draft, measures }, notices } : { draft, notices };
}

const articled = (word) => (/^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`);
const lowerFirst = (text) => (typeof text === 'string' && text ? text.charAt(0).toLowerCase() + text.slice(1) : text);
const sameDraft = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// ------------------------------------------------------------- the view

function verbWord(op, dated) {
	if (dated && own(DATED_VERBS, op)) return DATED_VERBS[op];
	return VERBS[op] || op;
}

/** The measure step's verbs: every op, each enabled where some field of this card answers it. */
function verbOptions(m, ctx) {
	const fields = (ctx && ctx.fields) || [];
	const current = fieldNamed(ctx, m.element);
	return MEASURE_OPS.map((op) => {
		if (op === 'count') return { value: op, label: countWords(ctx), disabled: false, reason: null };
		const able = fields.filter((f) => has(f.description.measures, op));
		const dated = current ? isDated(current) : (able.length > 0 && able.every(isDated));
		const label = `${verbWord(op, dated)}…`;
		if (able.length) return { value: op, label, disabled: false, reason: null };
		const where = ctx && ctx.cardName ? `the ${ctx.cardName} card` : 'a chart of every card';
		const reason = ctx && ctx.card
			? `Nothing on ${where} can be ${OP_VERBS[op]}.`
			: `Pick a card to measure its elements: ${where} counts items.`;
		return { value: op, label, disabled: true, reason };
	});
}

/** The elements a verb takes on this card, then the ones it can't, each with why. */
function elementOptions(m, ctx, refusedElements) {
	if (!m || m.op === 'count') return [];
	const fields = (ctx && ctx.fields) || [];
	const able = [];
	const unable = [];
	fields.forEach((f) => {
		if (f.system && !has(f.description.measures, m.op)) return;
		const refused = refusedElements && own(refusedElements, f.name) ? refusedElements[f.name] : null;
		if (refused) { unable.push({ value: f.name, label: f.label, disabled: true, reason: refused }); return; }
		if (has(f.description.measures, m.op)) able.push({ value: f.name, label: f.label, disabled: false, reason: null });
		else unable.push({ value: f.name, label: f.label, disabled: true, reason: f.entriesReason || refusalFor(m.op, f.description, columnWords(f)) });
	});
	return [...able, ...unable];
}

/** What the table's sentences call a field: a column by its own label ("Qty belongs to each Line entry…"), anything else by its label. */
function columnWords(field) {
	return field && field.column ? elementLabel(field.element) || field.element.name : field && field.label;
}

function unitChoice(m, field) {
	if (!field || field.description.kind !== 'measurement' || !field.description.measure) return null;
	const options = (field.element && field.element.options) || {};
	return { measure: field.description.measure, defaultUnit: options.defaultUnit || null, perUnit: has(field.description.notes, 'perUnit') };
}

function currencyChoice(m, field) {
	if (!field || field.description.kind !== 'money') return null;
	const options = (field.element && field.element.options) || {};
	const allowed = allowedCodes(options);
	// One code: nothing to choose. None listed: any code keepr knows.
	if (allowed.length === 1) return null;
	return { codes: allowed.length ? allowed : null, defaultCode: defaultCodeOf(options) };
}

// What a pill offered but refused says in the {{ }} menu (keepr-api's
// not_offered_yet unitsAndMoney, shortened to fit a menu).
const NOT_YET_IN_MENU = "charts can't calculate with money or measurements yet";
const QUANTITY_TYPES = ['currency', 'measurement'];

/**
 * What a per-item calculation's {{ }} button offers: the card's elements as
 * pills (no walk to another item — charts refuse a hop), then Created and
 * Updated. An element whose name a formula can't write (a `.` reads as a hop,
 * a brace ends the reference) is left out; money and measurements are listed,
 * disabled, saying why — the run refuses a formula that reads them.
 */
function perItemTokens(ctx) {
	const elements = formulaElements(ctx).filter((el) => referable(el.name));
	const byToken = new Map(elements.map((el) => [`{{${el.name}}}`, el]));
	return buildFormulaTokens(elements, null, { walks: false }).map((node) => {
		const el = node.token ? byToken.get(node.token) : null;
		if (!el || !has(QUANTITY_TYPES, el.dataType)) return node;
		return { ...node, disabled: true, menuLabel: `${node.label} — ${NOT_YET_IN_MENU}` };
	});
}

/** A per-item calculation's verbs: what its result can take (the capability table), the rest disabled with the server's reason. */
function perItemVerbOptions(m, check) {
	// What the formula answers decides the verbs whenever the module could
	// type it — a held one (a saved not_offered_yet) included: the exemption
	// is from the run's refusal, never from the verb check (the A8 re-check's R-1).
	const description = resultDescription(check);
	const dated = Boolean(description) && has(DATED_KINDS, check.type.type);
	const name = (typeof m.label === 'string' && m.label.trim()) || 'This calculation';
	return MEASURE_OPS.filter((op) => op !== 'count').map((op) => {
		const label = `${verbWord(op, dated)}…`;
		if (!description || has(description.measures, op)) return { value: op, label, disabled: false, reason: null };
		return { value: op, label, disabled: true, reason: refusalFor(op, description, name) };
	});
}

/** What a per-item formula's result offers (the capability table's description), when the module typed it — or null. */
function resultDescription(check) {
	return check && (check.state === 'ok' || heldCheck(check)) && check.description ? check.description : null;
}

/** What a finished formula says about itself under the editor, in one line. */
function formulaStatus(m, check) {
	if (!check || check.state !== 'ok') return null;
	if (isFormulaMeasure(m)) return 'Worked out from the measures it reads, for each value the chart draws.';
	const words = own(RESULT_WORDS, check.type.type) ? RESULT_WORDS[check.type.type] : 'a value';
	return check.type.type === 'text'
		? `Answers ${words} for each item. Text can only be counted.`
		: `Answers ${words} for each item.`;
}

function measureRow(m, i, draft, ctx, refusedElements, checks, read, removable) {
	if (m.keep) {
		const keep = m.keep;
		const text = typeof keep.perItem === 'string' ? keep.perItem : (typeof keep.formula === 'string' ? keep.formula : null);
		// The calculation in words (KPR-952): a formula across measures names each
		// measure by its label, a per-item formula each element by its label.
		const { names } = formulaChecks(draft, ctx);
		const words = text === null ? null : keptFormulaWords(text, typeof keep.perItem === 'string' ? (key) => { const f = fieldNamed(ctx, key); return f ? f.label : null; } : (key) => names.get(key) || null);
		return { index: i, key: m.key, kind: 'kept', keep: true, label: defaultMeasureLabel(m, ctx, draft), formula: text, words, hidden: keep.hidden === true, removable };
	}
	const check = checks[i];
	if (isFormulaMeasure(m)) {
		const { names } = formulaChecks(draft, ctx);
		return {
			index: i,
			key: m.key,
			kind: 'formula',
			keep: false,
			removable,
			// The measures above it, as pills by their labels; one that answers
			// money or a measurement is listed disabled (the run refuses it).
			tokens: draft.measures.slice(0, i).filter(Boolean).map((x) => {
				const token = { token: `{{${x.key}}}`, label: names.get(x.key) || x.key };
				const result = answerOf(x, ctx, checks[draft.measures.indexOf(x)]).result;
				return result === 'money' || result === 'measure' ? { ...token, disabled: true, menuLabel: `${token.label} — ${NOT_YET_IN_MENU}` } : token;
			}),
			check,
			status: formulaStatus(m, check),
			clock: Boolean(check && check.state === 'ok' && check.timeDependent),
			defaultLabel: defaultMeasureLabel(m, ctx, draft),
		};
	}
	if (isPerItemMeasure(m)) {
		const ok = Boolean(check && check.state === 'ok');
		const zeroable = ok && check.description.zeroable && m.op !== 'countNonBlank' && m.op !== 'distinct';
		return {
			index: i,
			key: m.key,
			kind: 'perItem',
			keep: false,
			removable,
			verbs: perItemVerbOptions(m, check),
			check,
			status: formulaStatus(m, check),
			clock: ok && check.timeDependent,
			blanks: zeroable ? { element: null } : null,
			hideable: read.has(m.key),
			shownWhileUnread: m.hideWish === true && !read.has(m.key),
			defaultLabel: defaultMeasureLabel(m, ctx, draft),
			needsTime: m.op === 'latest' && !fieldNamed(ctx, draft.time.on),
		};
	}
	const field = m.op === 'count' ? null : fieldNamed(ctx, m.element);
	const zeroable = Boolean(field) && field.description.zeroable && m.op !== 'countNonBlank' && m.op !== 'distinct';
	return {
		index: i,
		key: m.key,
		kind: 'element',
		keep: false,
		removable,
		verbs: verbOptions(m, ctx),
		elements: elementOptions(m, ctx, refusedElements),
		field,
		blanks: zeroable ? { element: field.label } : null,
		unit: unitChoice(m, field),
		currency: currencyChoice(m, field),
		hideable: read.has(m.key),
		shownWhileUnread: m.hideWish === true && !read.has(m.key),
		defaultLabel: defaultMeasureLabel(m, ctx, draft),
		needsTime: m.op === 'latest' && !fieldNamed(ctx, draft.time.on),
	};
}

function groupFieldOptions(draft, ctx, which) {
	const other = which === 'splitBy' ? draft.groupBy : null;
	return ((ctx && ctx.fields) || []).map((f) => {
		const g = groupOfField(f);
		let reason = null;
		if (f.reserved) reason = `"${f.name}" is a name the filter keeps for itself, so a chart can't group by an element called that.`;
		else if (!g) {
			if (f.description.kind === 'group') reason = `${f.label} is a repeating group: group by one of its columns.`;
			else if (f.description.kind === 'card') reason = 'Every item here is of one card: it has no child cards to group by.';
			else if (f.description.kind === 'collection') reason = 'This collection has no sub-collections to group by.';
			else if (!f.system) reason = `${f.label} is ${f.description.label}; a chart can't group by it.`;
			else return null;
		} else if (other && other.on === f.name && (!Array.isArray(g.buckets) || g.kind !== 'time')) {
			reason = 'The chart is grouped by it already.';
		}
		return { value: f.name, label: f.label, disabled: Boolean(reason), reason };
	}).filter(Boolean)
		// What can be chosen first, then what can't, each in the card's order.
		.sort((a, b) => Number(a.disabled) - Number(b.disabled));
}

function bucketOptions(draft, group, g, taken = null) {
	const range = draft.time && draft.time.on === group.on ? draft.time.range : undefined;
	const offered = (g.buckets || []).map((bucket) => {
		// A split on the group's own date can't take the group's bucket: every bar would be one color.
		if (taken && bucket === taken) {
			return { value: bucket, label: BUCKET_WORDS[bucket] || bucket, disabled: true, reason: `The chart is grouped by ${bucketPhrase(bucket)} already.` };
		}
		if (range !== undefined) {
			const n = estimateBuckets(range, bucket);
			if (n !== null && n > LIMITS.buckets) {
				return { value: bucket, label: BUCKET_WORDS[bucket] || bucket, disabled: true, reason: `${rangeLabel(range)} by ${bucketPhrase(bucket)} could be ${n.toLocaleString('en-US')} bars; a chart draws at most ${LIMITS.buckets}.` };
			}
		}
		return { value: bucket, label: BUCKET_WORDS[bucket] || bucket, disabled: false, reason: null };
	});
	const waiting = (g.waiting || []).map((w) => ({
		value: w.bucket,
		label: BUCKET_WORDS[w.bucket] || w.bucket,
		disabled: true,
		// AK gave the cyclic buckets their doors, and DOOR_REASONS lost `cyclic`:
		// a bucket the table still holds back says only that.
		reason: 'Not offered yet.',
	}));
	return [...offered, ...waiting];
}

function binsUnit(field) {
	if (!field || !field.element) return null;
	const options = field.element.options || {};
	// A money range is typed in the currency's major units, a measurement's
	// in the element's default unit — what the server reads `bins.size` in.
	if (field.description.kind === 'money') {
		const code = defaultCodeOf(options);
		return code && getCurrency(code) ? { kind: 'money', code, text: code } : null;
	}
	if (field.description.kind === 'measurement') {
		const unit = options.defaultUnit ? getUnit(options.defaultUnit) : null;
		return unit ? { kind: 'measure', unit: unit.id, text: unit.symbol ? `${unit.label} (${unit.symbol})` : unit.label || unit.id } : null;
	}
	return null;
}

function groupView(draft, ctx, which) {
	const group = draft[which];
	const options = groupFieldOptions(draft, ctx, which);
	if (!group) return { options, current: null };
	const field = fieldNamed(ctx, group.on);
	const g = groupOfField(field);
	if (!field || !g) return { options, current: null };
	const isSplit = which === 'splitBy';
	const topMax = g.top ? (isSplit ? Math.min(g.top.max, LIMITS.series) : g.top.max) : null;
	const topDefault = g.top ? (g.top.default ? Math.min(g.top.default, topMax) : null) : null;
	const multi = Array.isArray(field.description.multi) ? field.description.multi : null;
	let door = null;
	const exact = doorFor(g.kind, { dataType: field.description.dataType, multiple: field.description.multiple, multi: group.multi, bucket: group.bucket, perUnit: Boolean(g.perUnit) });
	if (multi || !exact.exact) door = exact.exact ? { exact: true, text: 'These bars open their items in the list.' } : { exact: false, text: exact.message };
	return {
		options,
		current: {
			field,
			kind: g.kind,
			buckets: Array.isArray(g.buckets)
				? bucketOptions(draft, group, g, isSplit && draft.groupBy && draft.groupBy.on === group.on ? draft.groupBy.bucket : null)
				: null,
			bins: g.kind === 'bins' ? { unit: binsUnit(field), perUnit: Boolean(g.perUnit) } : null,
			top: g.top ? { max: topMax, defaultValue: topDefault } : null,
			multi,
			door,
			multiValued: field.description.multiple && g.kind !== 'time',
			// Every item has a card and a collection: nothing to show as Not set.
			blanks: g.kind !== 'card' && g.kind !== 'collection',
		},
	};
}

function timeView(draft, ctx) {
	const fields = (ctx && ctx.fields) || [];
	const options = fields.filter(isTimeField).map((f) => ({ value: f.name, label: f.label }));
	const field = fieldNamed(ctx, draft.time.on);
	const multi = field && Array.isArray(field.description.multi) ? field.description.multi : null;
	let door = null;
	if (multi && draft.time.range) {
		// The range is ANDed into every door, and a list bounds each date of an
		// item separately (keepr-api spec.js `no_door` at `time`; doors.js
		// eachDateRange): read as the earliest or latest, nothing opens a list;
		// read as each, only a bar of the chart's own dates does.
		door = draft.time.multi === 'each'
			? { exact: false, text: 'An item can hold dates inside the range and outside it, so only a bar of each date opens its items.' }
			: { exact: false, text: DOOR_REASONS.severalDatesReading };
	}
	return { options, field, multi, door };
}

/** Every control key the builder shows now, for placing a server refusal where it belongs. */
function visibleKeys(draft, view) {
	const keys = new Set(['general', 'card', 'measures', 'groupBy', 'splitBy', 'filter', 'time', 'show', 'show.type', 'name', 'description']);
	if (view.subCollections) keys.add('subCollections');
	if (view.counting) keys.add('entries');
	view.measures.forEach((row) => {
		keys.add(`measures.${row.index}`);
		if (row.keep) return;
		keys.add(`measures.${row.index}.label`);
		if (row.kind === 'formula') {
			keys.add(`measures.${row.index}.formula`);
			return;
		}
		keys.add(`measures.${row.index}.op`);
		if (row.kind === 'perItem') keys.add(`measures.${row.index}.perItem`);
		else if (draft.measures[row.index].op !== 'count') keys.add(`measures.${row.index}.element`);
		if (row.blanks) keys.add(`measures.${row.index}.blanks`);
		if (row.unit) keys.add(`measures.${row.index}.unit`);
		if (row.currency) keys.add(`measures.${row.index}.currency`);
		if (row.hideable) keys.add(`measures.${row.index}.hidden`);
	});
	[['groupBy', view.group], ['splitBy', view.split]].forEach(([which, g]) => {
		if (which === 'splitBy' && !draft.groupBy) return;
		keys.add(which);
		keys.add(`${which}.on`);
		const c = g.current;
		if (!c) return;
		if (c.buckets) keys.add(`${which}.bucket`);
		if (c.bins) keys.add(`${which}.bins`);
		if (c.top) keys.add(`${which}.top`);
		if (c.multi) keys.add(`${which}.multi`);
		if (c.blanks) keys.add(`${which}.blanks`);
	});
	keys.add('time.on');
	if (draft.time.on) keys.add('time.range');
	if (view.time.multi) keys.add('time.multi');
	Object.keys(view.formOptions).forEach((option) => {
		keys.add(option === 'compare' ? 'time.compare' : `show.${option}`);
	});
	return keys;
}

/**
 * What the question still lacks before it can be asked, as `{ key, message }`
 * on the control that lacks it — or null. The preview waits for it; Save
 * says it on the control.
 */
/**
 * What each calculation still lacks or the server would refuse, beside its
 * own field — every one at once, not only the first: `{ index, key, tone,
 * text }`, tone 'error' for what the server would refuse (the analyzer's
 * words, a lookup hop, money or a measurement, a verb the result can't take),
 * 'hint' for what is simply not written yet.
 */
function formulaIssues(draft, ctx, checks) {
	const out = [];
	draft.measures.forEach((m, i) => {
		const check = checks[i];
		if (!check || m.keep) return;
		const field = isFormulaMeasure(m) ? 'formula' : 'perItem';
		const key = `measures.${i}.${field}`;
		// The verb against what the formula answers runs FIRST, for a held
		// formula too: keepr-api refuses a verb the result can't take
		// (measure_not_allowed) when the spec is saved, before the run's
		// not_offered_yet that holding exempts (the A8 re-check's R-1: an
		// exemption still runs every check that doesn't depend on it).
		const description = isPerItemMeasure(m) ? resultDescription(check) : null;
		if (description && !has(description.measures, m.op)) {
			const name = (typeof m.label === 'string' && m.label.trim()) || 'This calculation';
			out.push({ index: i, key: `measures.${i}.op`, tone: 'error', text: `${refusalFor(m.op, description, name)} Choose another.` });
		}
		if (check.state === 'empty') {
			out.push({ index: i, key, tone: 'hint', text: isFormulaMeasure(m) ? 'Write the formula, with the measures it reads from the {{ }} button.' : 'Write the calculation, with the elements it reads from the {{ }} button.' });
		} else if (check.state === 'blocked') {
			out.push({ index: i, key, tone: 'hint', text: check.message });
		} else if (isHeld(m, check, draft)) {
			// Saved this way (through the API, by an assistant) and untouched: the
			// server stores it and the run refuses it. It is said, the preview
			// waits, and it never stops a save of what the person did change
			// (the A8 review's I-1). Changed or new, it is refused like any other.
			out.push({ index: i, key, tone: 'note', held: true, text: check.message });
			out.push({ index: i, key, tone: 'note', held: true, text: 'It is kept as saved, and the chart can\'t be drawn until this formula changes.' });
		} else if (check.state === 'refused') {
			const near = Number.isInteger(check.position) ? ` Near character ${check.position + 1}.` : '';
			out.push({ index: i, key, tone: 'error', text: `${check.message}${near}` });
		}
	});
	return out;
}

/**
 * A saved calculation the run refuses (`not_offered_yet`) that the person has
 * not changed — nor, for a formula across measures, any measure it reads.
 */
function isHeld(m, check, draft) {
	if (!heldCheck(check) || !unchanged(m)) return false;
	if (!isFormulaMeasure(m)) return true;
	const asSaved = (key) => draft.measures.some((x) => x && x.key === key && (x.keep || unchanged(x)));
	return (check.reads || []).every(asSaved);
}

function incompleteOf(draft, ctx, forms, issues = []) {
	for (let i = 0; i < draft.measures.length; i += 1) {
		const m = draft.measures[i];
		if (m.keep) continue;
		const issue = issues.find((x) => x.index === i && !x.held);
		if (issue) return { key: issue.key, message: issue.text };
		if (isFormulaMeasure(m)) continue;
		if (isPerItemMeasure(m) && countedGroup(ctx)) return { key: `measures.${i}.perItem`, message: perItemOnEntries(ctx) };
		if (isPerItemMeasure(m)) {
			if (m.op === 'latest' && !fieldNamed(ctx, draft.time.on)) return { key: `measures.${i}.op`, message: '"Latest value" needs the chart\'s date, to know which item is newest. Choose one under Time.' };
			continue;
		}
		if (m.op !== 'count' && !m.element) return { key: `measures.${i}.element`, message: `Choose the element to ${own(TO_VERBS, m.op) ? TO_VERBS[m.op] : 'measure'}.` };
		if (m.op === 'latest' && !fieldNamed(ctx, draft.time.on)) return { key: `measures.${i}.op`, message: '"Latest value" needs the chart\'s date, to know which item is newest. Choose one under Time.' };
	}
	const check = (which) => {
		const g = draft[which];
		if (!g) return null;
		const field = fieldNamed(ctx, g.on);
		const group = groupOfField(field);
		if (group && group.kind === 'bins' && g.binsMode === 'size' && !(typeof g.binsSize === 'number' && g.binsSize > 0)) {
			return { key: `${which}.bins`, message: 'Choose how wide each range is.' };
		}
		return null;
	};
	const groupGap = check('groupBy') || (draft.groupBy ? check('splitBy') : null);
	if (groupGap) return groupGap;
	if (!forms.length) return { key: 'show.type', message: 'Nothing about this chart can be drawn yet. Check what it measures and how it groups.' };
	if (draft.show.type === 'progress' && !(typeof draft.show.goal === 'number' && Number.isFinite(draft.show.goal))) {
		return { key: 'show.goal', message: 'A progress meter needs a goal.' };
	}
	return null;
}

/**
 * Everything the builder's steps show for this draft: per step, what is
 * offered, what is disabled and why, and what a choice opens. The steps
 * render this and nothing else, so what the screen offers and what the
 * tests read are one rule.
 *
 *   ctx.hasSubCollections   the collection has sub-collections (the Include switch)
 *   ctx.refusedElements     { name: sentence } elements the server refused as colliding
 */
export function builderView(draft, ctx = {}) {
	const forms = formsFor(draft, ctx);
	const { checks } = formulaChecks(draft, ctx);
	const read = keysReadByFormulas(draft);
	const plain = draft.measures.filter((m) => !takesFormulaSlot(m)).length;
	const formulas = draft.measures.length - plain;
	// The chart keeps one measure that is not a formula across measures
	// (keepr-api spec.js no_measures), so its last one has no ✕.
	const removable = (m) => draft.measures.length > 1 && (takesFormulaSlot(m) || plain > 1);
	const fullPlain = `A chart holds at most ${LIMITS.plainMeasures} measures.`;
	const groups = ctx.card ? countableGroups(ctx.elements) : [];
	const view = {
		subCollections: Boolean(ctx.hasSubCollections),
		// What the chart counts (KPR-684): its items, or a repeating group's
		// entries — offered only on a card that holds a group.
		counting: groups.length
			? {
				value: draft.entries || null,
				// `group`: the group's own label, for the words the Items step says about it (KPR-936).
				options: [{ value: null, label: 'Items' }, ...groups.map((g) => ({ value: g.name, label: `${g.label} entries`, group: g.label }))],
			}
			: null,
		measures: draft.measures.map((m, i) => measureRow(m, i, draft, ctx, ctx.refusedElements, checks, read, removable(m))),
		canAddMeasure: plain < LIMITS.plainMeasures,
		// "Calculate per item…" (A8): a per-item calculation counts as a measure; a chart of every card has none.
		canCalculate: !ctx.card
			? { offered: false, reason: "A chart of every card can't calculate per item. Pick a card first." }
			: (countedGroup(ctx)
				? { offered: false, reason: perItemOnEntries(ctx) }
				: (plain < LIMITS.plainMeasures ? { offered: true, reason: null } : { offered: false, reason: fullPlain })),
		// "Add formula measure" (A8): at most two.
		canAddFormula: formulas < LIMITS.formulaMeasures
			? { offered: true, reason: null }
			: { offered: false, reason: `A chart holds at most ${LIMITS.formulaMeasures} formulas across measures.` },
		// What a per-item calculation's {{ }} button offers: the card's elements,
		// no walk to another item (charts refuse a lookup hop), then Created and Updated.
		elementTokens: ctx.card ? perItemTokens(ctx) : [],
		group: groupView(draft, ctx, 'groupBy'),
		split: draft.groupBy ? groupView(draft, ctx, 'splitBy') : { options: [], current: null },
		time: timeView(draft, ctx),
		forms,
		recommended: forms[0] || null,
		formOptions: formOptionRules(draft, ctx),
		goal: goalUnits(draft, ctx),
	};
	view.formulaIssues = formulaIssues(draft, ctx, checks);
	view.incomplete = incompleteOf(draft, ctx, forms, view.formulaIssues);
	// A saved calculation the run refuses, untouched: the preview waits on it;
	// saving does not (`{ key, text }`, the first one).
	view.held = view.formulaIssues.find((x) => x.held) || null;
	view.visible = visibleKeys(draft, view);
	return view;
}

// ------------------------------------------------------------- refusals

const STEP_OF = Object.freeze({
	general: 'general',
	card: 'items',
	entries: 'items',
	subCollections: 'items',
	measures: 'measures',
	groupBy: 'groupBy',
	splitBy: 'splitBy',
	filter: 'filter',
	time: 'time',
	show: 'show',
	name: 'name',
	description: 'name',
});

/** The step a control key sits in. */
export function stepOf(key) {
	// The comparison is a `time` key drawn among the form's options.
	if (key === 'time.compare') return 'show';
	const root = typeof key === 'string' ? key.split('.')[0] : '';
	return own(STEP_OF, root) ? STEP_OF[root] : 'general';
}

/**
 * Which control a server refusal belongs to (plan §6.2: every refusal is
 * shown on the field its `path` names). `measures[2].element` is the third
 * measure's element; a part the screen does not show right now falls back to
 * the nearest thing it does — the measure's row, the step — so a refusal is
 * never invisible. The comparison is a `time.*` path drawn among the form's
 * options.
 */
export function refusalKey(path, view) {
	const visible = view && view.visible instanceof Set ? view.visible : new Set();
	let p = typeof path === 'string' ? path.trim().replace(/\[(\d+)\]/g, '.$1') : '';
	if (!p || p === 'spec' || p === 'v') return 'general';
	if (p === 'card_id' || p === 'collection') return 'card';
	if (p === 'entries') return visible.has('entries') ? 'entries' : 'card';
	if (p === 'subCollections') return visible.has('subCollections') ? 'subCollections' : 'card';
	if (p === 'filter' || p.indexOf('filter.') === 0) return 'filter';
	const parts = p.split('.');
	// Longest visible prefix.
	while (parts.length) {
		p = parts.join('.');
		if (visible.has(p)) return p;
		parts.pop();
	}
	return 'general';
}

/** The range presets the time step offers, "All time" first as no range. */
export function rangeOptions() {
	return Object.keys(RANGE_PRESET_LABELS).filter((preset) => preset !== 'all');
}

// --------------------------------------------------------- names and forms

/**
 * The name a new chart is offered until its author types one: its first
 * measure, then what it is grouped and split by — "Total of Amount by
 * month", "Count items by Status and Kind" — held to the server's 100.
 */
export function suggestName(draft, ctx) {
	const first = (draft.measures || []).find((m) => m && !(m.keep && m.keep.hidden) && m.hidden !== true);
	let text = first ? measureName(first, ctx, draft) : 'Chart';
	const phrase = (group) => {
		const field = fieldNamed(ctx, group.on);
		if (!field) return fieldWords(group.on);
		const g = groupOfField(field);
		if (g && g.kind === 'time' && group.bucket) {
			const word = bucketPhrase(group.bucket);
			return field.name === 'created' ? word : `${word} of ${field.label}`;
		}
		return field.label;
	};
	if (draft.groupBy) text += ` by ${phrase(draft.groupBy)}`;
	if (draft.groupBy && draft.splitBy) text += ` and ${phrase(draft.splitBy)}`;
	return text.length > 100 ? `${text.slice(0, 99)}…` : text;
}

const FORM_NEEDS = Object.freeze({
	number: 'Shows one value: it takes no Group by.',
	progress: 'Shows one value against a goal: it takes no Group by.',
	bar: 'Needs a Group by.',
	line: 'Needs a Group by a date.',
	area: 'Needs a Group by a date, and one measure with no Split by.',
	donut: 'Shows a count or a total by a category or a range of values, in six parts at most, with no Split by.',
	heatmap: 'Needs a Group by and a Split by, with at most 60 values on each.',
	calendar: 'Needs a Group by a date, by day, and no Split by.',
	table: 'Shows any question.',
});

/**
 * Why each form this question can't take is not offered, in one line each
 * (the "Why not other forms?" list under Show as) — the forms' own needs,
 * plus the reason that applies to this question when one stands out.
 */
export function formReasons(draft, ctx, forms) {
	const shape = shapeOf(draft, ctx);
	const visible = shape.measures.filter((m) => !m.hidden);
	const dated = visible.some((m) => m.result === 'date' || m.result === 'time');
	const several = visible.length > 1;
	const axes = new Set(visible.map((m) => m.axis));
	return ['number', 'progress', 'bar', 'line', 'area', 'donut', 'heatmap', 'calendar', 'table']
		.filter((form) => !has(forms, form))
		.map((form) => {
			let reason = FORM_NEEDS[form];
			if (dated && form !== 'number' && form !== 'table') reason = 'An earliest or latest date is shown as a number or a table.';
			else if (several && (axes.size > 1 || visible.some((m) => m.mixed))) reason = 'Measures in different units or currencies share only a table.';
			else if (several && (form === 'number' || form === 'progress' || form === 'donut' || form === 'area' || form === 'calendar' || form === 'heatmap')) reason = 'Several measures share a bar, a line or a table.';
			else if (form === 'donut' && visible[0] && !has(PART_OF_WHOLE_OPS, visible[0].op)) reason = 'A donut shows parts of a whole: a count or a total, never an average or a share.';
			else if (form === 'heatmap' && visible[0] && visible[0].mixed) reason = 'A heat map needs one currency or unit.';
			return { form, word: chartFormWord(form), reason };
		});
}
